import React, { useState, useMemo } from "react";
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  TrophyIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  BoltIcon,
  ChartPieIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";

// --- GLOBAL MOCK DATA FOR FIELD PERFORMANCE ---
const MOCK_FIELD_KPIS = {
    totalVisits: 854,
    onTimeRate: 92, // %
    avgServiceTime: "45 min", // Avg time spent at verified location
    geoCompliance: 98, // % of visits verified by GPS
    topPerformer: "Priya Sharma",
    recentViolations: 3,
};

const MOCK_VISIT_DATA = [
    { type: "Completed Visits", value: 854, color: "bg-green-600", icon: ClipboardDocumentCheckIcon },
    { type: "Pending (Scheduled)", value: 120, color: "bg-yellow-600", icon: ClockIcon },
    { type: "Service Violations", value: 26, color: "bg-red-600", icon: BoltIcon },
];

const MOCK_FIELD_AGENTS = [
    { id: 1, name: "Arjun Singh", region: "North", visits: 180, onTime: 95, compliance: 99, score: 92 },
    { id: 2, name: "Priya Sharma", region: "West", visits: 210, onTime: 98, compliance: 98, score: 95 },
    { id: 3, name: "Vikram Kumar", region: "East", visits: 150, onTime: 85, compliance: 92, score: 80 },
    { id: 4, name: "Ravi Menon", region: "South", visits: 164, onTime: 90, compliance: 97, score: 88 },
    { id: 5, name: "Sonia Das", region: "Central", visits: 140, onTime: 89, compliance: 95, score: 85 },
    { id: 6, name: "Jatin Patel", region: "North", visits: 120, onTime: 92, compliance: 94, score: 87 },
];

// --- HELPER COMPONENTS ---

// KPI Card Component (Field Metrics)
const FieldMetricCard = ({ title, value, icon: Icon, colorClass, footer }) => (
    <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-100 flex flex-col justify-between h-full transition duration-300 hover:shadow-indigo-200/50 hover:border-indigo-500 transform hover:-translate-y-0.5">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-poppins font-medium text-gray-500">{title}</p>
                <p className="text-3xl font-poppins font-bold text-gray-900 mt-2">{value}</p>
            </div>
            <div className={`p-3 rounded-xl ${colorClass}/10`}>
                <Icon className={`h-7 w-7 ${colorClass}`} />
            </div>
        </div>
        <p className="text-xs font-roboto mt-4 text-gray-400 border-t pt-2">{footer}</p>
    </div>
);

// Custom Select Component (Reusable)
const CustomSelect = ({ label, options, selected, onChange }) => (
    <div className="flex flex-col">
        <label className="text-xs font-poppins font-semibold text-gray-600 mb-1">{label}</label>
        <select
            value={selected}
            onChange={onChange}
            className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm font-roboto focus:ring-indigo-500 focus:border-indigo-500 transition duration-150 shadow-sm"
        >
            {options.map((option, index) => (
                <option key={index} value={option.value || option.label}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
);

// Visit Breakdown Chart (Simulated Donut/Bar)
const VisitBreakdownChart = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);

    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 h-full flex flex-col">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <ChartPieIcon className="h-6 w-6 mr-2 text-blue-600"/>
                Monthly Visits Breakdown
            </h3>
            
            <div className="space-y-4">
                {data.map((item, index) => (
                    <div key={index}>
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-roboto text-gray-700 break-words flex items-center">
                                <item.icon className={`h-4 w-4 mr-2 ${item.color}`} />
                                {item.type}
                            </span>
                            <span className="text-sm font-poppins font-semibold text-gray-900 flex-shrink-0">
                                {item.value} ({total > 0 ? ((item.value / total) * 100).toFixed(1) : 0}%)
                            </span>
                        </div>
                        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                                className={`h-full ${item.color} rounded-full transition-all duration-1000 ease-out`} 
                                style={{ width: `${total > 0 ? (item.value / total) * 100 : 0}%` }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>

            <p className="mt-6 text-sm text-gray-500 font-roboto border-t pt-4">Total scheduled activities: {total}.</p>
        </div>
    );
};


// Agent Performance Leaderboard
const AgentLeaderboard = ({ data, isFiltering }) => {
    
    // Sort agents by performance score descending
    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => b.score - a.score);
    }, [data]);

    if (isFiltering) {
        return (
            <div className="text-center py-20 text-indigo-500 bg-white rounded-xl shadow-lg mt-6">
                <ArrowPathIcon className="h-8 w-8 mx-auto animate-spin mb-4" />
                <p className="text-lg font-poppins">Loading leaderboard data...</p>
            </div>
        );
    }
    
    return (
        <div className="bg-white p-6 rounded-xl shadow-2xl border border-gray-100 h-full"> 
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-6 flex items-center">
                <TrophyIcon className="h-6 w-6 mr-2 text-yellow-600"/>
                Agent Performance Leaderboard
            </h3>

            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100/70">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider w-1/4">Rank</th>
                            <th className="px-4 py-3 text-left text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Agent</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">Visits</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider">On-Time (%)</th>
                            <th className="px-4 py-3 text-center text-xs font-poppins font-bold text-gray-700 uppercase tracking-wider font-extrabold">Score</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100 font-roboto">
                        {sortedData.map((agent, index) => (
                            <tr key={agent.id} className="hover:bg-blue-50 transition duration-100"> 
                                <td className="px-4 py-3 text-sm text-center">
                                    <span className={`font-extrabold text-white px-3 py-1 rounded-lg shadow-md ${
                                        index === 0 ? 'bg-amber-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-700' : 'bg-gray-400'
                                    }`}>
                                        {index + 1}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900 font-semibold">{agent.name}</td>
                                <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-gray-700">
                                    {agent.visits}
                                </td>
                                <td className="px-4 py-3 text-center whitespace-nowrap text-sm text-indigo-600">
                                    {agent.onTime}%
                                </td>
                                <td className="px-4 py-3 text-center whitespace-nowrap text-sm font-extrabold text-green-700">
                                    {agent.score}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// --- MAIN APPLICATION COMPONENT ---

export default function FieldPerformanceApp() {
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [selectedPeriod, setSelectedPeriod] = useState('month');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const regionOptions = [
        { label: "All Regions", value: "all" },
        { label: "North Region", value: "North" },
        { label: "South Region", value: "South" },
        { label: "East Region", value: "East" },
        { label: "West Region", value: "West" },
    ];

    const periodOptions = [
        { label: "Last 30 Days", value: "month" },
        { label: "Last 7 Days", value: "week" },
        { label: "Year to Date", value: "ytd" },
    ];

    const handleRefresh = () => {
        setIsRefreshing(true);
        console.log("Refreshing field performance data...");
        // Simulate data refresh from API
        setTimeout(() => setIsRefreshing(false), 1200); 
    };

    const handleFilterChange = (setter, value) => {
        setter(value);
        handleRefresh(); // Trigger data refresh/filtering
    };

    const filteredAgents = useMemo(() => {
        if (selectedRegion === 'all' || isRefreshing) return MOCK_FIELD_AGENTS;
        
        return MOCK_FIELD_AGENTS.filter(agent => agent.region === selectedRegion);
    }, [selectedRegion, MOCK_FIELD_AGENTS, isRefreshing]);


    return (
        <div 
            className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-10"
            // Setting the font family preference for the enterprise look
            style={{ fontFamily: "Poppins, Roboto, sans-serif" }}
        >
            {/* Page Header and Controls */}
            <div className="mb-8 border-b border-gray-200 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="mb-4 sm:mb-0">
                    <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 font-poppins flex items-center">
                        <MapPinIcon className="h-8 w-8 mr-3 text-red-600"/>
                        Field Employee Performance
                    </h1>
                    <p className="text-gray-500 mt-1 text-md font-roboto">
                        Track field activity, geo-compliance, and efficiency across all regions.
                    </p>
                </div>
                <button 
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className={`px-6 py-2.5 text-base font-semibold rounded-lg transition duration-300 shadow-xl flex items-center justify-center 
                        bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-300 transform active:scale-95 w-full sm:w-auto ${isRefreshing ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    {isRefreshing ? (
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                        <ArrowPathIcon className="h-5 w-5 mr-2" />
                    )}
                    {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                </button>
            </div>

            {/* 1. Filter Bar */}
            <div className="bg-white p-5 rounded-xl shadow-lg border border-gray-100 flex flex-col md:flex-row items-stretch md:items-end gap-4 mb-8">
                <div className="flex-grow grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <CustomSelect
                        label="Select Reporting Period"
                        options={periodOptions}
                        selected={selectedPeriod}
                        onChange={(e) => handleFilterChange(setSelectedPeriod, e.target.value)}
                    />
                     <CustomSelect
                        label="Filter by Region"
                        options={regionOptions}
                        selected={selectedRegion}
                        onChange={(e) => handleFilterChange(setSelectedRegion, e.target.value)}
                    />
                    <div className="flex flex-col">
                        <label className="text-xs font-poppins font-semibold text-gray-600 mb-1">Download Reports</label>
                        <button 
                            className="w-full p-2 border border-teal-600 bg-teal-500/10 text-teal-700 font-bold rounded-lg text-sm hover:bg-teal-500/20 transition duration-150 shadow-sm"
                            onClick={() => alert('Report download initiated!')}
                        >
                            <span className="flex items-center justify-center"><MagnifyingGlassIcon className="h-4 w-4 mr-2" /> Generate Custom Report</span>
                        </button>
                    </div>
                </div>
            </div>


            {/* 2. Key Metrics Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                <FieldMetricCard 
                    title="Total Visits Completed" 
                    value={MOCK_FIELD_KPIS.totalVisits} 
                    icon={ClipboardDocumentCheckIcon}
                    colorClass="text-indigo-600" 
                    footer="Completed tasks verified by location."
                />
                <FieldMetricCard 
                    title="On-Time Arrival Rate" 
                    value={`${MOCK_FIELD_KPIS.onTimeRate}%`} 
                    icon={CheckCircleIcon} 
                    colorClass="text-green-600" 
                    footer="Percentage of arrivals within 15 min buffer."
                />
                <FieldMetricCard 
                    title="Avg. Service Time" 
                    value={MOCK_FIELD_KPIS.avgServiceTime} 
                    icon={ClockIcon} 
                    colorClass="text-yellow-600" 
                    footer="Mean time spent at client/work location."
                />
                <FieldMetricCard 
                    title="Geo-Compliance Rate" 
                    value={`${MOCK_FIELD_KPIS.geoCompliance}%`} 
                    icon={MapPinIcon} 
                    colorClass="text-red-600" 
                    footer="Verified location tracking adherence."
                />
            </div>

            {/* 3. Charts and Leaderboard (2-column layout) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Visits Breakdown Chart (1/3 width on large screens) */}
                <div className="lg:col-span-1">
                    <VisitBreakdownChart data={MOCK_VISIT_DATA}/>
                </div>
                
                {/* Agent Leaderboard Table (2/3 width on large screens) */}
                <div className="lg:col-span-2">
                    <AgentLeaderboard data={filteredAgents} isFiltering={isRefreshing}/>
                </div>
            </div>
            
            {/* 4. Footer */}
            <div className="mt-10 pt-4 border-t border-gray-200 text-center">
                <p className="text-sm text-gray-500 font-roboto">
                    Performance metrics are calculated based on geo-fencing, task closure status, and predefined time schedules.
                </p>
            </div>

        </div>
    );
}